import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  ShoppingBag, 
  Truck, 
  CreditCard, 
  Shield, 
  ChevronRight,
  Check,
  MapPin,
  Mail,
  Phone,
  User,
  Lock,
  Package,
  Zap,
  Clock
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SHIPPING_OPTIONS = [
  { id: 'standard', name: 'Standard Shipping', price: 8.99, freeOver: 75, days: '5-7 business days', icon: Truck },
  { id: 'express', name: 'Express Shipping', price: 14.99, freeOver: null, days: '2-3 business days', icon: Zap },
  { id: 'overnight', name: 'Overnight Shipping', price: 29.99, freeOver: null, days: '1 business day', icon: Clock },
];

const PremiumCheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cart, clearCart } = useCart();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedShipping, setSelectedShipping] = useState('standard');
  
  const [formData, setFormData] = useState({
    email: user?.email || '',
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    phone: user?.phone || '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States'
  });

  // Check if cancelled
  useEffect(() => {
    if (searchParams.get('cancelled')) {
      toast.error('Payment Cancelled', {
        description: 'Your payment was cancelled. Your cart items are still saved.'
      });
    }
  }, [searchParams]);

  const subtotal = cart.subtotal || 0;
  
  // Calculate shipping based on selected method
  const getShippingCost = () => {
    const selectedMethod = SHIPPING_OPTIONS.find(opt => opt.id === selectedShipping);
    if (!selectedMethod) return 8.99;
    
    // Free shipping for standard over $75
    if (selectedMethod.id === 'standard' && subtotal >= 75) {
      return 0;
    }
    return selectedMethod.price;
  };
  
  const shipping = subtotal > 0 ? getShippingCost() : 0;
  const total = subtotal + shipping;

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateStep1 = () => {
    if (!formData.email || !formData.firstName || !formData.lastName) {
      toast.error('Please fill in all required fields');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.address || !formData.city || !formData.state || !formData.zip) {
      toast.error('Please fill in all address fields');
      return false;
    }
    return true;
  };

  const proceedToPayment = async () => {
    if (!validateStep2()) return;
    
    setIsProcessing(true);
    
    try {
      const cartItems = cart.items.map(item => {
        const product = item.product || {};
        return {
          product_id: item.product_id,
          name: product.name || item.name,
          price: product.sale_price || product.price || item.price,
          quantity: item.quantity,
          image: product.images?.[0] || item.image
        };
      });

      const response = await axios.post(`${API}/api/payments/create-checkout`, {
        origin_url: window.location.origin,
        cart_items: cartItems,
        shipping_address: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          address: formData.address,
          apartment: formData.apartment,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          country: formData.country,
          phone: formData.phone
        },
        email: formData.email,
        user_id: user?.id || user?._id || null,
        shipping_method: selectedShipping
      });

      if (response.data.checkout_url) {
        // Redirect to Stripe
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Checkout Failed', {
        description: error.response?.data?.detail || 'Unable to process checkout. Please try again.'
      });
      setIsProcessing(false);
    }
  };

  if (cart.items?.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-[#F5F1EB] rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="h-12 w-12 text-[#8B7355]" />
          </div>
          <h1 className="text-3xl font-bold text-[#2C2824] mb-4">Your Cart is Empty</h1>
          <p className="text-[#6B6560] mb-8">Add some products to your cart to proceed with checkout.</p>
          <Button 
            onClick={() => navigate('/shop')}
            className="bg-[#5D6B55] hover:bg-[#4A5745] text-white px-8 py-3 text-lg"
          >
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <div className="bg-white border-b border-[#E8E4DE]">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#5D6B55] rounded-full flex items-center justify-center">
                <span className="text-white text-xl font-bold">U</span>
              </div>
              <span className="text-xl font-bold text-[#2C2824]">BOXVERRA</span>
            </button>
            
            {/* Progress Steps */}
            <div className="hidden md:flex items-center gap-4">
              <StepIndicator number={1} label="Contact" active={step >= 1} completed={step > 1} />
              <ChevronRight className="h-4 w-4 text-[#C4BFB8]" />
              <StepIndicator number={2} label="Shipping" active={step >= 2} completed={step > 2} />
              <ChevronRight className="h-4 w-4 text-[#C4BFB8]" />
              <StepIndicator number={3} label="Payment" active={step >= 3} completed={step > 3} />
            </div>

            <div className="flex items-center gap-2 text-[#5D6B55]">
              <Shield className="h-5 w-5" />
              <span className="text-sm font-medium">Secure Checkout</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm border border-[#E8E4DE] overflow-hidden">
              {/* Step 1: Contact Information */}
              <div className={`${step === 1 ? 'block' : 'hidden'}`}>
                <div className="p-6 border-b border-[#E8E4DE] bg-[#FDFCFA]">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#5D6B55] rounded-full flex items-center justify-center">
                      <Mail className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-[#2C2824]">Contact Information</h2>
                      <p className="text-sm text-[#6B6560]">We'll send your order confirmation here</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-6 space-y-5">
                  {/* Guest Checkout Notice */}
                  {!user && (
                    <div className="bg-[#F5F1EB] border border-[#E8E4DE] rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-[#5D6B55] rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-[#2C2824] font-medium">Checkout as Guest</p>
                          <p className="text-xs text-[#6B6560] mt-1">
                            You can complete your order without an account. 
                            <button 
                              onClick={() => navigate('/login?redirect=/checkout')}
                              className="text-[#5D6B55] font-semibold hover:underline ml-1"
                            >
                              Sign in
                            </button>
                            {' '}for faster checkout and order tracking.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Logged in user badge */}
                  {user && (
                    <div className="bg-[#E8F5E2] border border-[#5D6B55]/20 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#5D6B55] rounded-full flex items-center justify-center">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                        <div>
                          <p className="text-sm text-[#2C2824] font-medium">Signed in as {user.email}</p>
                          <p className="text-xs text-[#5D6B55]">Your order will be saved to your account</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="email" className="text-[#2C2824] font-medium mb-2 block">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="you@example.com"
                      className="h-12 border-[#E8E4DE] focus:border-[#5D6B55] focus:ring-[#5D6B55] text-[#2C2824]"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName" className="text-[#2C2824] font-medium mb-2 block">
                        First Name *
                      </Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="John"
                        className="h-12 border-[#E8E4DE] focus:border-[#5D6B55] focus:ring-[#5D6B55] text-[#2C2824]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName" className="text-[#2C2824] font-medium mb-2 block">
                        Last Name *
                      </Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Doe"
                        className="h-12 border-[#E8E4DE] focus:border-[#5D6B55] focus:ring-[#5D6B55] text-[#2C2824]"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-[#2C2824] font-medium mb-2 block">
                      Phone Number (for delivery updates)
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+1 (555) 000-0000"
                      className="h-12 border-[#E8E4DE] focus:border-[#5D6B55] focus:ring-[#5D6B55] text-[#2C2824]"
                    />
                  </div>

                  <Button 
                    onClick={() => validateStep1() && setStep(2)}
                    className="w-full bg-[#5D6B55] hover:bg-[#4A5745] text-white h-14 text-lg font-semibold mt-4"
                  >
                    Continue to Shipping
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Step 2: Shipping Address */}
              <div className={`${step === 2 ? 'block' : 'hidden'}`}>
                <div className="p-6 border-b border-[#E8E4DE] bg-[#FDFCFA]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#5D6B55] rounded-full flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-[#2C2824]">Shipping Address</h2>
                        <p className="text-sm text-[#6B6560]">Where should we deliver your order?</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setStep(1)}
                      className="text-[#5D6B55] hover:text-[#4A5745] text-sm font-medium"
                    >
                      Edit Contact
                    </button>
                  </div>
                </div>

                {/* Contact Summary */}
                <div className="px-6 py-4 bg-[#F5F1EB] border-b border-[#E8E4DE]">
                  <div className="flex items-center gap-2 text-[#2C2824]">
                    <User className="h-4 w-4 text-[#8B7355]" />
                    <span className="font-medium">{formData.firstName} {formData.lastName}</span>
                    <span className="text-[#6B6560]">•</span>
                    <span className="text-[#6B6560]">{formData.email}</span>
                  </div>
                </div>
                
                <div className="p-6 space-y-5">
                  <div>
                    <Label htmlFor="address" className="text-[#2C2824] font-medium mb-2 block">
                      Street Address *
                    </Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="123 Main Street"
                      className="h-12 border-[#E8E4DE] focus:border-[#5D6B55] focus:ring-[#5D6B55] text-[#2C2824]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="apartment" className="text-[#2C2824] font-medium mb-2 block">
                      Apartment, suite, etc. (optional)
                    </Label>
                    <Input
                      id="apartment"
                      name="apartment"
                      value={formData.apartment}
                      onChange={handleInputChange}
                      placeholder="Apt 4B"
                      className="h-12 border-[#E8E4DE] focus:border-[#5D6B55] focus:ring-[#5D6B55] text-[#2C2824]"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city" className="text-[#2C2824] font-medium mb-2 block">
                        City *
                      </Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="New York"
                        className="h-12 border-[#E8E4DE] focus:border-[#5D6B55] focus:ring-[#5D6B55] text-[#2C2824]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state" className="text-[#2C2824] font-medium mb-2 block">
                        State *
                      </Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="NY"
                        className="h-12 border-[#E8E4DE] focus:border-[#5D6B55] focus:ring-[#5D6B55] text-[#2C2824]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="zip" className="text-[#2C2824] font-medium mb-2 block">
                        ZIP Code *
                      </Label>
                      <Input
                        id="zip"
                        name="zip"
                        value={formData.zip}
                        onChange={handleInputChange}
                        placeholder="10001"
                        className="h-12 border-[#E8E4DE] focus:border-[#5D6B55] focus:ring-[#5D6B55] text-[#2C2824]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country" className="text-[#2C2824] font-medium mb-2 block">
                        Country
                      </Label>
                      <Input
                        id="country"
                        name="country"
                        value={formData.country}
                        disabled
                        className="h-12 border-[#E8E4DE] bg-[#F5F1EB] text-[#6B6560]"
                      />
                    </div>
                  </div>

                  {/* Shipping Method Selection */}
                  <div className="mt-6 space-y-3">
                    <h3 className="text-base font-semibold text-[#2C2824] flex items-center gap-2">
                      <Truck className="h-5 w-5 text-[#5D6B55]" />
                      Select Shipping Method
                    </h3>
                    
                    {SHIPPING_OPTIONS.map((option) => {
                      const IconComponent = option.icon;
                      const isFree = option.id === 'standard' && subtotal >= 75;
                      const displayPrice = isFree ? 0 : option.price;
                      
                      return (
                        <label
                          key={option.id}
                          className={`flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all border-2 ${
                            selectedShipping === option.id
                              ? 'border-[#5D6B55] bg-[#5D6B55]/5'
                              : 'border-[#E8E4DE] hover:border-[#5D6B55]/50 bg-white'
                          }`}
                        >
                          <input
                            type="radio"
                            name="shipping"
                            value={option.id}
                            checked={selectedShipping === option.id}
                            onChange={(e) => setSelectedShipping(e.target.value)}
                            className="hidden"
                          />
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selectedShipping === option.id
                              ? 'border-[#5D6B55]'
                              : 'border-[#C4BFB8]'
                          }`}>
                            {selectedShipping === option.id && (
                              <div className="w-3 h-3 rounded-full bg-[#5D6B55]" />
                            )}
                          </div>
                          <div className="w-10 h-10 bg-[#F5F1EB] rounded-full flex items-center justify-center flex-shrink-0">
                            <IconComponent className="h-5 w-5 text-[#5D6B55]" />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-[#2C2824]">{option.name}</p>
                            <p className="text-sm text-[#6B6560]">{option.days}</p>
                          </div>
                          <div className="text-right">
                            <span className={`font-bold ${displayPrice === 0 ? 'text-[#5D6B55]' : 'text-[#2C2824]'}`}>
                              {displayPrice === 0 ? 'FREE' : `$${displayPrice.toFixed(2)}`}
                            </span>
                            {isFree && option.id === 'standard' && (
                              <p className="text-xs text-[#5D6B55]">Order over $75</p>
                            )}
                          </div>
                        </label>
                      );
                    })}
                    
                    {subtotal < 75 && selectedShipping === 'standard' && (
                      <div className="bg-[#FFF8E7] rounded-lg p-3 text-center">
                        <p className="text-sm text-[#8B7355]">
                          Add <span className="font-bold">${(75 - subtotal).toFixed(2)}</span> more for FREE standard shipping!
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3 mt-6">
                    <Button 
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="flex-1 h-14 border-[#E8E4DE] text-[#2C2824] hover:bg-[#F5F1EB]"
                    >
                      Back
                    </Button>
                    <Button 
                      onClick={proceedToPayment}
                      disabled={isProcessing}
                      className="flex-[2] bg-[#5D6B55] hover:bg-[#4A5745] text-white h-14 text-lg font-semibold"
                    >
                      {isProcessing ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <Lock className="mr-2 h-5 w-5" />
                          Pay ${total.toFixed(2)}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="mt-6 flex flex-wrap justify-center gap-6 text-[#6B6560]">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-[#5D6B55]" />
                <span className="text-sm">SSL Encrypted</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-[#5D6B55]" />
                <span className="text-sm">Secure Payment</span>
              </div>
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-[#5D6B55]" />
                <span className="text-sm">30-Day Returns</span>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-[#E8E4DE] sticky top-4">
              <div className="p-6 border-b border-[#E8E4DE]">
                <h2 className="text-xl font-bold text-[#2C2824] flex items-center gap-2">
                  <ShoppingBag className="h-5 w-5 text-[#8B7355]" />
                  Order Summary
                </h2>
              </div>

              {/* Cart Items */}
              <div className="p-6 space-y-4 max-h-80 overflow-y-auto">
                {cart.items?.map((item, index) => {
                  const product = item.product || {};
                  const price = product.sale_price || product.price || item.price || 0;
                  const imageUrl = product.images?.[0] || item.image || '/placeholder.png';
                  
                  return (
                    <div key={index} className="flex gap-4">
                      <div className="relative">
                        <div className="w-20 h-20 bg-[#F5F1EB] rounded-lg overflow-hidden">
                          <img 
                            src={imageUrl} 
                            alt={product.name || item.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="absolute -top-2 -right-2 w-6 h-6 bg-[#5D6B55] text-white text-xs rounded-full flex items-center justify-center font-bold">
                          {item.quantity}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-[#2C2824] text-sm line-clamp-2">{product.name || item.name}</h3>
                        <p className="text-sm text-[#6B6560] mt-1">Qty: {item.quantity}</p>
                      </div>
                      <p className="font-semibold text-[#2C2824]">
                        ${(item.item_total || price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Totals */}
              <div className="p-6 border-t border-[#E8E4DE] space-y-3 bg-[#FDFCFA]">
                <div className="flex justify-between text-[#6B6560]">
                  <span>Subtotal ({cart.item_count} items)</span>
                  <span className="text-[#2C2824] font-medium">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#6B6560]">
                  <span>Shipping</span>
                  <span className={shipping === 0 ? 'text-[#5D6B55] font-medium' : 'text-[#2C2824]'}>
                    {shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}
                  </span>
                </div>
                <div className="h-px bg-[#E8E4DE]" />
                <div className="flex justify-between text-lg font-bold text-[#2C2824]">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>

              {/* Free Shipping Progress */}
              {subtotal < 75 && subtotal > 0 && (
                <div className="px-6 pb-6">
                  <div className="bg-[#FFF8E7] rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Truck className="h-4 w-4 text-[#D4A574]" />
                      <span className="text-sm font-medium text-[#8B7355]">
                        ${(75 - subtotal).toFixed(2)} away from FREE shipping!
                      </span>
                    </div>
                    <div className="w-full bg-[#E8E4DE] rounded-full h-2">
                      <div 
                        className="bg-[#D4A574] h-2 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min((subtotal / 75) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step Indicator Component
const StepIndicator = ({ number, label, active, completed }) => (
  <div className="flex items-center gap-2">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all
      ${completed ? 'bg-[#5D6B55] text-white' : 
        active ? 'bg-[#5D6B55] text-white' : 
        'bg-[#E8E4DE] text-[#6B6560]'}`}
    >
      {completed ? <Check className="h-4 w-4" /> : number}
    </div>
    <span className={`text-sm font-medium ${active ? 'text-[#2C2824]' : 'text-[#6B6560]'}`}>
      {label}
    </span>
  </div>
);

export default PremiumCheckoutPage;
